import React, { useEffect, useState } from 'react';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { convertOrderNumToDisplay, splitByNewLine, stageLabel } from '../libs/utils';
import { TextOnly } from '../components/Text';

const validInteger = /^\d+$/;
const validFloat = /^\d+(\.\d{1,2})?$/;

const InvoicePrintView: React.FC<any> = (props) => {
  const { currentShop, orderData, customer, vehicle} = props;
  const [displayParts, setDisplayParts] = useState<any[]>([]);
  const [displayServices, setDisplayServices] = useState<any[]>([]);
  const [invoiceAgreement, setInvoiceAgreement] = useState(props.invoiceAgreemnet || (currentShop?.crmSettings?.invoiceAgreement || '').length===0 ? TextOnly('defaultAgreementStatement'): currentShop?.crmSettings?.invoiceAgreement);
  const invoiceAgreementMaxWidth = 720; // 730px - 5px padding on both sides
  const invoiceAgreementFont = "12px Roboto, Helvetica, Arial, sans-serif";
  const invoiceAgreementLetterSpacing = 0.2;
  const invoiceAgreementLineHeight = 15;

  const [totalLabor, setTotalLabor] = useState(0);
  const [totalParts, setTotalParts] = useState(0);
  const [estimatedTax, setEstimatedTax] = useState(0);

  // Invoice agreement font size is 12px
  const getTextWidth = (text: string, font: string, letterSpacing = 0) => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (context) {
      context.font = font;
      const baseWidth = context.measureText(text).width;
      // Add letter spacing manually
      const extraSpacing = (text.length - 1) * letterSpacing;
      return baseWidth + extraSpacing;
    } else {
      return -1;
    }
  }

  const getLineCount = (text: string, maxWidth: number, font: string, letterSpacing = 0) => {
    if (!text) return 0;

    // Split by explicit line breaks first
    const linesByBreaks = text.split(/\r?\n/);
    let totalLines = 0;

    for (const line of linesByBreaks) {
      const words = line.split(/\s+/);
      if (words.length === 0 || (words.length === 1 && words[0] === "")) {
        // Empty line still counts as 1 line
        totalLines += 1;
        continue;
      }

      let lines = 1;
      let currentLine = "";

      for (let i = 0; i < words.length; i++) {
        const word = words[i];

        // Try adding the next word
        const testLine = currentLine ? currentLine + " " + word : word;
        const testWidth = getTextWidth(testLine, font, letterSpacing);

        if (testWidth <= maxWidth) {
          currentLine = testLine;
        } else {
          // If a single word is longer than maxWidth, force break it
          if (!currentLine) {
            let partial = "";

            for (let char of word) {
              const testPartial = partial + char;
              const partialWidth = getTextWidth(testPartial, font, letterSpacing);

              if (partialWidth > maxWidth) {
                lines++;
                partial = char;
              } else {
                partial = testPartial;
              }
            }

            currentLine = partial;
          } else {
            // Move word to next line
            lines++;
            currentLine = word;
          }
        }
      }

      totalLines += lines;
    }

    return totalLines;
  };

  const numLines = getLineCount(invoiceAgreement, invoiceAgreementMaxWidth, invoiceAgreementFont, invoiceAgreementLetterSpacing);

  useEffect(() => {
    const orderBody = document.getElementById('order-body');
    const orderSignature = document.getElementById('order-signature');
    const agreementStatement = document.getElementById('agreement-statement');

    if (orderBody && orderSignature && agreementStatement) {
      if (numLines <= 4) {
        orderBody.style.height = "700px";
        orderSignature.style.height = "116px";
        agreementStatement.style.height = "70px";
      } else {
        const extraLines = numLines-4;
        orderBody.style.height = `${700 - extraLines*invoiceAgreementLineHeight}px`;
        orderSignature.style.height = `${116 + extraLines*invoiceAgreementLineHeight}px`;
        agreementStatement.style.height = `${70 + extraLines*invoiceAgreementLineHeight}px`;
      }
    }

  }, [invoiceAgreement]);

  useEffect(() => {
    props.invoiceAgreement && setInvoiceAgreement(props.invoiceAgreement);
  }, [props.invoiceAgreement])


  const createDisplayPartList = () => {
    const parts: any[] = [];

    orderData.services?.forEach((service: any) => {
      service.parts?.forEach((part: any) => {
        parts.push(
          <>
            {
              currentShop?.crmSettings?.showPartsPrices ?
                <div className="parts-container" key={part.perPartID}>
                  <span className='p part-item'>{part.partName}</span>
                  <span className='p part-item line-item-right'>{part.quantity}</span>
                  <span className='p part-item line-item-right padding-right-10px'>${part.retailPrice}</span>
                </div>
                :
                <span key={part.perPartID} className='p part-item'>{part.partName}
                  <span className='floatRight padding-right-10px line-item-right'>{part.quantity}</span>
                </span>
            }
          </>
      )
      })
    });

    orderData.parts?.forEach((part: any, i: number) => {
      parts.push(
        <>
          {
            currentShop?.crmSettings?.showPartsPrices ?
              <div className="parts-container" key={part.perPartID}>
                <span className='p part-item'>{part.partName}</span>
                <span className='p part-item line-item-right'>{part.quantity}</span>
                <span className='p part-item line-item-right padding-right-10px'>${part.retailPrice}</span>
              </div>
              :
              <span key={part.perPartID} className='p part-item'>{part.partName}
                <span className='floatRight padding-right-10px line-item-right'>{part.quantity}</span>
              </span>
          }
        </>
      )
    });

    setDisplayParts(parts);
  }
  const createDisplayServiceList = () => {
    const services: any[] = [];
    orderData.services?.forEach((service: any, i: number) => {
        services?.push(
        <span key={service.perServiceID} className='p service-item'>
          - {service.title}
          <span className='floatRight padding-right-10px line-item-right'>${(service.price ?? 0).toFixed(2)}</span>
        </span>
      );
    });
    setDisplayServices(services);
  }

  useEffect(() => {
    createDisplayPartList();
    createDisplayServiceList();
  }, [orderData]);

  useEffect(() => {
    let tempTotalLabor = 0;
    let tempTotalParts = 0;
    let tempEstimatedTax = 0;

    const taxExempt = orderData?.taxExempt || false;

    const partTaxRate = parseFloat(currentShop?.crmSettings?.partsTaxRate || '0') / 100;
    const laborTaxRate = parseFloat(currentShop?.crmSettings?.laborTaxRate || '0') / 100;

    const serviceObjs = orderData?.services ?? [];
    const partObjs = orderData?.parts ?? [];
    const laborObjs = orderData?.labors ?? [];

    if (serviceObjs.length) {
      serviceObjs.forEach((s: any) => {
        const service = s;
        let serviceTax = 0;

        if (service.title?.length > 0) {
          const serviceLabor = service?.labors?.reduce((sum: number, labor: any) => {

            if (validFloat.test(labor.laborHours) && validFloat.test(labor.laborRate)) {
              const laborPrice = labor.laborHours * labor.laborRate;
              if (!taxExempt) {
                serviceTax +=  laborPrice * laborTaxRate;
              }

              return sum + laborPrice;
            } else {
              return sum;
            }
          }, 0) || 0;

          const serviceParts = service?.parts?.reduce((sum: number, part: any) => {
            if (((part.partNumber?.length > 0 && part.partName?.length > 0 && part.retailPrice) || part.partID) && part.quantity) {
              const quantity = validInteger.test(part.quantity) && part.quantity > 0 ? String(part.quantity) : '0';
              let retailPrice = part.retailPrice || 0;
              const partPrice = parseFloat(retailPrice) * parseInt(quantity);
              if (!taxExempt) {
                serviceTax += partPrice * partTaxRate;
              }
              return sum + partPrice;
            }
          }, 0) || 0;

          tempEstimatedTax += serviceTax;
          tempTotalLabor += serviceLabor;
          tempTotalParts += serviceParts;
        }
      });
    }

    if (partObjs.length) {
      partObjs.forEach((part: any) => {
        const quantity = validInteger.test(part.quantity) && part.quantity > 0 ? String(part.quantity) : '0';
        const partPrice = parseFloat(part.retailPrice || 0) * parseInt(quantity);
        const partTax = !taxExempt ? partPrice * partTaxRate: 0;
        tempEstimatedTax += partTax;
        tempTotalParts += partPrice;
      });
    }

    if (laborObjs.length) {
      laborObjs.forEach((labor: any) => {
        const hours = validFloat.test(labor.laborHours) ? labor.laborHours: '0'
        const rate = validFloat.test(labor.laborRate) ? labor.laborRate: '0';
        const laborPrice = parseFloat(hours) * parseFloat(rate);
        const laborTax = !taxExempt ? laborPrice * laborTaxRate: 0;
        tempEstimatedTax += laborTax;
        tempTotalLabor += laborPrice;
      });
    }

    setTotalLabor(tempTotalLabor);
    setTotalParts(tempTotalParts);
    setEstimatedTax(tempEstimatedTax);
  }, [orderData]);

  return (
    <div className='letter-page' id={`print-element-${orderData?.orderID}`}>
      <header>
        <span className='order-number'>{stageLabel(orderData)?.toLocaleUpperCase()} {convertOrderNumToDisplay(orderData.orderNumber)}</span>
        <div className='shop-info'>
          <span className='padding-bottom-20px'>
            <span className='shop-name'>{currentShop.shopName}</span><br />
            {currentShop?.addressInfo?.streetAddress1}<br />
            {currentShop?.addressInfo?.streetAddress2 && <>{currentShop.addressInfo.streetAddress2}<br /></>}
            {currentShop?.addressInfo?.city}, {currentShop?.addressInfo?.state} {currentShop?.addressInfo?.zip}<br/>
            {currentShop?.phone}
          </span>
        </div>

        {currentShop.shopLogoUrl &&
          <img src={`${currentShop.shopLogoUrl}`} 
            id={'shopLogo'}
            className='shop-logo'
            crossOrigin='anonymous'
          />
        }
      </header>

      <div className='invoice-print-view-body'>
        <div>
          <div id='top-row'>
            <div id='left' className='l-flex-column'>
              <span className='bold-text l-display-block'>{customer?.lastName}, {customer?.firstName}</span>
              <span className='bold-text l-display-block'>{customer?.email || customer?.phoneNumber || <br />}</span>
            </div>
            <div id='right'>
              <span className='bold-text l-display-block'>{vehicle?.make} {vehicle?.model} {vehicle?.year}</span>
              <span className='bold-text l-display-block'>Lic #: {(vehicle?.licensePlate || '') + ' '}</span>
            </div>
          </div>

          <section id='order-body' className='border-bottom border-top u-position-relative'>
            <div id='second-row'>
              <div className='left'>
                <div className='text border-bottom u-padding-bottom-none'>
                  {
                    currentShop?.crmSettings?.showPartsPrices ?
                      <div className="parts-container bold-text">
                        <span className='p'>{TextOnly('parts')}</span>
                        <span className='p line-item-right'>{TextOnly('qty')}</span>
                        <span className='p line-item-right padding-right-10px'>{TextOnly('price')}</span>
                      </div>
                      :
                      <span className='p bold-text'>
                        {TextOnly('parts')}
                        <div className='floatRight padding-right-10px line-item-right'>{TextOnly('qty')}</div>
                      </span>
                  }
                </div>
              </div>
              <div className='right border-left'>
                <div className='text border-bottom u-padding-bottom-none'>
                  <span className='p bold-text padding-left-10px u-margin-bottom-small u-margin-top-small'>{TextOnly('description')}</span>
                </div>
              </div>
            </div>

            <div id='third-row'>
              <div className='left padding-top-10px'>
                {displayParts}
                <div>
                  <span className='p bold-text services-header border-top border-bottom'>
                    {TextOnly('services')}
                    <span className='price-header floatRight padding-right-10px line-item-right'>{TextOnly('price')}</span>
                  </span>
                </div>
                  {displayServices}
              </div>

              <div className='right border-left padding-top-10px'>
                <span className='p remove-p-margin padding-left-10px'>{splitByNewLine(orderData.description)}</span>
              </div>
            </div>

            <div className='price-table'>
              <span className='p'>{TextOnly('labor')} <span className='floatRight'>${totalLabor.toFixed(2)}</span></span>
              <span className='p'>{TextOnly('parts')} <span className='floatRight'>${totalParts.toFixed(2)}</span></span>
              <span className='p'>{TextOnly('subtotal')} <span className='floatRight'>${(totalParts + totalLabor).toFixed(2)}</span></span>
              <span className='p'>{TextOnly('tax')} <span className='floatRight'>${estimatedTax.toFixed(2)}</span></span>
              <span className='p text-bold'>{TextOnly('total')} <span className='floatRight'>${(totalParts + totalLabor + estimatedTax).toFixed(2)}</span></span>
            </div>
          </section>

          <section id='order-signature'>
            <div id='agreement-statement'>
              <span>{(invoiceAgreement || '').length===0 ? TextOnly('defaultAgreementStatement'): invoiceAgreement}</span>
            </div>
            <table>
              <tbody>
                <tr>
                  <td><span className='p' id='signature-text'>{TextOnly('signature')}:</span></td>
                  <td><span className='p' id='signature-line'>&nbsp;</span></td>

                  <td><span className='p' id='date-text'>{TextOnly('date')}:</span></td>
                  <td><span className='p' id='date-line'>&nbsp;</span></td>
                </tr>
            </tbody>
          </table>
          </section>

        </div>
      </div>
    </div >
  )
}

export default InvoicePrintView;